const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parseToolResult = (content: string): unknown => {
  const separator = content.startsWith('200 OK') ? content.indexOf('\n\n') : -1;
  const body = separator >= 0 ? content.slice(separator + 2) : content;

  try {
    return JSON.parse(body) as unknown;
  } catch {
    return undefined;
  }
};

const isApiResponse = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) &&
  isRecord(value.status) &&
  typeof value.status.code === 'number' &&
  typeof value.status.message === 'string' &&
  ('data' in value || 'error' in value);

export const parseToolResultObject = (
  content: string,
): Record<string, unknown> | undefined => {
  const parsed = parseToolResult(content);
  const data = isApiResponse(parsed) ? parsed.data : parsed;
  return isRecord(data) ? data : undefined;
};

export const parseToolResultEnvelopeObject = (
  content: string,
): Record<string, unknown> | undefined => {
  const parsed = parseToolResult(content);
  return isApiResponse(parsed) && isRecord(parsed.data)
    ? parsed.data
    : undefined;
};

const getResponseMessages = (response: Record<string, unknown>): string[] => {
  const warnings = Array.isArray(response.warnings)
    ? response.warnings.filter(
        (warning): warning is string => typeof warning === 'string',
      )
    : [];
  const message = typeof response.message === 'string' ? response.message : '';
  return [message, ...warnings].filter(Boolean);
};

export const formatToolResultMessages = (content: string): string => {
  const parsed = parseToolResult(content);
  return isApiResponse(parsed) ? getResponseMessages(parsed).join('\n\n') : '';
};

export const formatToolResultContent = (content: string): string => {
  const parsed = parseToolResult(content);
  if (!isApiResponse(parsed)) {
    return isRecord(parsed) || Array.isArray(parsed)
      ? JSON.stringify(parsed, null, 2)
      : content;
  }

  const data = parsed.data;
  const error = parsed.error;
  let body: string | undefined;
  if (data !== undefined && data !== null) {
    body = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  } else if (isRecord(error) && typeof error.message === 'string') {
    body = error.message;
    if (error.details !== undefined) {
      body += `\n${JSON.stringify(error.details, null, 2)}`;
    }
  }

  return (
    [body, ...getResponseMessages(parsed)].filter(Boolean).join('\n\n') ||
    content
  );
};

export const getToolInput = (input: unknown): Record<string, unknown> =>
  input && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};

export const getInputString = (
  input: Record<string, unknown>,
  key: string,
  { preserveWhitespace = false }: { preserveWhitespace?: boolean } = {},
): string | undefined => {
  const value = input[key];
  if (typeof value !== 'string') return undefined;
  return preserveWhitespace ? value : value.trim() || undefined;
};

export const truncateToolSubject = (subject: string): string =>
  subject.length > 60 ? `${subject.slice(0, 59)}…` : subject;
